import React, { useEffect, useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog.jsx';
import { Progress } from '../components/ui/progress.jsx';
import { Button } from '../components/ui/button.jsx';
import { 
  Loader2, 
  Check, 
  Download, 
  Music, 
  Mic2, 
  Drum,
  Piano,
  Guitar,
  Waves,
  CheckCircle,
  AlertCircle,
  Upload
} from 'lucide-react';
import api from '../services/api';

const STEM_ICONS = {
  drum: Drum,
  bass: Waves,
  piano: Piano,
  electric_guitar: Guitar,
  acoustic_guitar: Guitar,
  synthesizer: Waves,
  strings: Music,
  wind: Waves,
};

const STEM_NAMES = {
  drum: 'Drum',
  bass: 'Bass',
  piano: 'Piano',
  electric_guitar: 'Electric Guitar',
  acoustic_guitar: 'Acoustic Guitar',
  synthesizer: 'Synthesizer',
  strings: 'Strings',
  wind: 'Wind',
};

function StemIndicator({ stemType, currentStem, completedStems, isComplete }) {
  const Icon = STEM_ICONS[stemType] || Music;
  const name = STEM_NAMES[stemType] || stemType;
  
  const isPast = completedStems?.includes(stemType);
  const isCurrent = stemType === currentStem && !isComplete;
  
  return (
    <div className={`flex items-center gap-3 py-1.5 transition-all duration-300 ${
      isPast ? 'text-green-500' : 
      isCurrent ? 'text-primary font-medium' : 
      'text-muted-foreground/50'
    }`}>
      <div className={`w-7 h-7 rounded-full flex items-center justify-center transition-all duration-300 ${
        isPast ? 'bg-green-500/20' : 
        isCurrent ? 'bg-primary/20' : 
        'bg-muted/30'
      }`}>
        {isPast ? (
          <Check className="w-3.5 h-3.5" />
        ) : isCurrent ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : (
          <Icon className="w-3.5 h-3.5" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <span className="text-sm font-medium">{name}</span>
        {isCurrent && (
          <p className="text-xs text-muted-foreground mt-0.5 truncate">Separating {name.toLowerCase()}...</p>
        )}
      </div>
      {isPast && (
        <span className="text-xs text-green-500 flex-shrink-0">Complete</span>
      )}
    </div>
  );
}

export default function StemProgressModal({ isOpen, sessionId, selectedStems, onClose, onComplete }) {
  const [progress, setProgress] = useState({
    phase: 'starting',
    message: 'Starting stem separation...',
    percent: 0,
    current_stem: null,
    completed_stems: []
  });
  const [errorMessage, setErrorMessage] = useState(null);
  const pollIntervalRef = useRef(null);

  useEffect(() => {
    if (!isOpen || !sessionId) {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      return;
    }

    const pollStatus = async () => {
      try {
        const response = await api.get(`/sessions/${sessionId}/stems-status`);
        const data = response.data;
        
        if (data.stems_progress) {
          setProgress(data.stems_progress);
        }
        
        if (data.stems_progress?.phase === 'error') {
          setErrorMessage(data.stems_progress.message || 'An error occurred during stem separation');
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
        }
        
        if (data.stems_progress?.phase === 'complete') {
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
          if (data.stems) {
            onComplete?.(data.stems);
          }
        }
      } catch (error) {
        console.error('Error polling stems status:', error);
      }
    };

    pollStatus();
    pollIntervalRef.current = setInterval(pollStatus, 1000);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [isOpen, sessionId, onComplete]);

  useEffect(() => {
    if (isOpen) {
      setProgress({
        phase: 'starting',
        message: 'Starting stem separation...',
        percent: 0,
        current_stem: null,
        completed_stems: []
      });
      setErrorMessage(null);
    }
  }, [isOpen]);

  const currentPhase = progress.phase || 'starting';
  const isComplete = currentPhase === 'complete';
  const isError = currentPhase === 'error';

  const handleClose = () => {
    onClose();
  };

  const getPhaseTitle = () => {
    switch (currentPhase) {
      case 'starting':
        return 'Initializing';
      case 'downloading':
        return 'Downloading Audio';
      case 'uploading':
        return 'Uploading to Processor';
      case 'separating':
        return 'Separating Stems';
      case 'complete':
        return 'Complete';
      case 'error':
        return 'Error';
      default:
        return 'Processing';
    }
  };

  const getPhaseIcon = () => {
    switch (currentPhase) {
      case 'downloading':
        return Download;
      case 'uploading':
        return Upload;
      case 'complete':
        return CheckCircle;
      case 'error':
        return AlertCircle;
      default:
        return Music;
    }
  };

  const PhaseIcon = getPhaseIcon();

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="stem-progress-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            {!isComplete && !isError && (
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
            )}
            {isComplete && <CheckCircle className="h-5 w-5 text-green-500" />}
            {isError && <AlertCircle className="h-5 w-5 text-red-500" />}
            <span>
              {isComplete ? 'Stem Separation Complete!' : 
               isError ? 'Separation Failed' : 
               'Separating Stems'}
            </span>
          </DialogTitle>
          <DialogDescription>
            {isComplete ? 'All stems have been separated. You can now download individual instruments.' :
             isError ? 'There was a problem separating the stems.' :
             'Using AI to isolate individual instrument tracks from your audio...'}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">{progress.message}</span>
              <span className="text-primary font-medium">{progress.percent}%</span>
            </div>
            <Progress value={progress.percent} className="w-full h-2" />
          </div>
          
          <div className="border rounded-lg p-3 bg-muted/20">
            <div className="flex items-center gap-2 mb-3 pb-2 border-b">
              <PhaseIcon className={`w-4 h-4 ${isComplete ? 'text-green-500' : isError ? 'text-red-500' : 'text-primary'}`} />
              <span className="text-sm font-medium">{getPhaseTitle()}</span>
            </div>
            
            <div className="space-y-0.5 max-h-[250px] overflow-y-auto">
              {selectedStems.map((stemType) => (
                <StemIndicator 
                  key={stemType} 
                  stemType={stemType}
                  currentStem={progress.current_stem}
                  completedStems={progress.completed_stems}
                  isComplete={isComplete}
                />
              ))}
            </div>
          </div>
          
          {isError && errorMessage && (
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20">
              <p className="text-sm text-red-500">{errorMessage}</p>
            </div>
          )}
          
          {isComplete && (
            <div className="flex justify-center pt-2">
              <Button onClick={handleClose} className="w-full">
                <Check className="h-4 w-4 mr-2" />
                View & Download Stems
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
